import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { LastChanceCollapsedForm } from '@/components/food-cost/FinalCtaSection'
import { FoodCostAuthoritySection } from '@/components/food-cost/FoodCostAuthoritySection'
import { FoodCostFaqSection } from '@/components/food-cost/FoodCostFaqSection'
import { FoodCostHeader } from '@/components/food-cost/FoodCostHeader'
import { FoodCostHero } from '@/components/food-cost/FoodCostHero'
import { FoodCostSideCharacter } from '@/components/food-cost/FoodCostSideCharacter'
import { HomeRouterPopup } from '@/components/food-cost/HomeRouterPopup'
import { HowItWorksFlowSection } from '@/components/food-cost/HowItWorksFlowSection'
import { PricingSection } from '@/components/food-cost/PricingSection'
import { WhatIsTipiSection } from '@/components/food-cost/WhatIsTipiSection'
import { buildAppOutboundUrl } from '@/components/food-cost/buildAppOutboundUrl'
import { FAQS_HASH, FOOD_COST_LANDING_PAGE, FORM_HASH } from '@/components/food-cost/constants'
import { NewsletterSubscribe } from '@/features/newsletter/NewsletterSubscribe'
import { LocaleSeoLinks } from '@/navigation/LocaleSeoLinks'
import { ROUTES, SITE_ORIGIN } from '@/navigation/constants'
import { absoluteLocalizedUrl } from '@/navigation/locale'
import { useLandingLocale } from '@/navigation/useLandingLocale'
import { Footer } from '@/shared/ui/Footer'
import { PageHelmet } from '@/shared/ui/PageHelmet'
import '@/styles/food-cost-fonts.css'

const OG_IMAGE = `${SITE_ORIGIN}/escandallo-restaurante.webp`
const TIPI_ORGANIZATION_ID = `${SITE_ORIGIN}/#organization`

export function FoodCostPage() {
    const { t } = useTranslation()
    const locale = useLandingLocale()
    const heroRef = useRef<HTMLElement>(null)
    const howItWorksRef = useRef<HTMLDivElement>(null)
    const characterSectionsRef = useRef<HTMLDivElement>(null)
    const [headerProgress, setHeaderProgress] = useState(0)
    const [searchParams] = useSearchParams()
    // The /empieza quiz is Spanish-only; visitors coming back from it already chose.
    const showRouterPopup = locale === 'es' && !searchParams.has('perfil')
    const heroSignupUrl = buildAppOutboundUrl('hero_cta')
    const headerSignupUrl = buildAppOutboundUrl('header_cta')

    const title = t('foodCost.meta.title', 'Software de escandallos con IA por voz para restaurantes | Tipi')
    const description = t(
        'foodCost.meta.description',
        '¿Sabes cuánto cuestan tus platos? Hacer escandallos nunca fue tan sencillo.',
    )
    const offerDescription = t(
        'foodCost.meta.offerDescription',
        'Empiezas hoy, pruebas Tipi y pagas a final de mes solo si te quedas: 139 €, o 89 € si empiezas en las primeras 24 horas. Si cancelas antes de 30 días, no pagas. IVA no incluido.',
    )
    const canonicalUrl = absoluteLocalizedUrl(ROUTES.home, locale)

    const scrollToSection = (hash: string) => {
        const target = document.querySelector(hash)
        target?.scrollIntoView({ behavior: 'smooth' })
    }

    useEffect(() => {
        const onScroll = () => {
            const howItWorksTop = howItWorksRef.current?.getBoundingClientRect().top

            if (typeof howItWorksTop !== 'number') return
            const fadeDistance = window.innerHeight * 0.2
            const nextProgress = Math.max(0, Math.min(1, (fadeDistance - howItWorksTop) / fadeDistance))
            setHeaderProgress(nextProgress)
        }

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const scrollToHashTarget = () => {
            const hash = window.location.hash
            if (hash === FORM_HASH || hash === FAQS_HASH) {
                scrollToSection(hash)
            }
        }

        scrollToHashTarget()
        window.addEventListener('hashchange', scrollToHashTarget)
        return () => window.removeEventListener('hashchange', scrollToHashTarget)
    }, [])

    return (
        <div className="min-h-screen overflow-x-clip bg-[#F5EEE4] font-['Archivo',system-ui,sans-serif] text-[#17130E] antialiased">
            <LocaleSeoLinks path={ROUTES.home} locale={locale} />
            <PageHelmet locale={locale}>
                <title>{title}</title>
                <meta name='description' content={description} />
                <meta property='og:type' content='website' />
                <meta property='og:title' content={title} />
                <meta property='og:description' content={description} />
                <meta property='og:image' content={OG_IMAGE} />
                <meta name='twitter:card' content='summary_large_image' />
                <meta name='twitter:url' content={canonicalUrl} />
                <meta name='twitter:title' content={title} />
                <meta name='twitter:description' content={description} />
                <meta name='twitter:image' content={OG_IMAGE} />
                <link
                    rel='preload'
                    href='/fonts/Archivo/archivo-latin.woff2'
                    as='font'
                    type='font/woff2'
                    crossOrigin='anonymous'
                />
                <script type='application/ld+json'>
                    {JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'SoftwareApplication',
                        name: 'Tipi',
                        alternateName: 'Oido Tipi',
                        url: canonicalUrl,
                        image: OG_IMAGE,
                        applicationCategory: 'BusinessApplication',
                        operatingSystem: 'Web, iOS, Android',
                        description,
                        offers: {
                            '@type': 'Offer',
                            price: '139',
                            priceCurrency: 'EUR',
                            description: offerDescription,
                        },
                        provider: { '@id': TIPI_ORGANIZATION_ID },
                    })}
                </script>
            </PageHelmet>

            <div className='mx-auto w-full overflow-x-clip'>
                <FoodCostHeader progress={headerProgress} signupUrl={headerSignupUrl} />
                <main className='overflow-x-clip'>
                    <FoodCostHero ref={heroRef} signupUrl={heroSignupUrl} />
                    <div ref={characterSectionsRef}>
                        <div ref={howItWorksRef} data-character-section data-foodcost-section='2'>
                            <WhatIsTipiSection />
                        </div>
                        {/* No side character here: it would cover the sticky feature cards. */}
                        <HowItWorksFlowSection />
                        <div data-character-section>
                            <FoodCostAuthoritySection />
                        </div>
                        <div data-character-section>
                            <PricingSection />
                        </div>
                        <div data-character-section>
                            <LastChanceCollapsedForm landingPage={FOOD_COST_LANDING_PAGE} />
                        </div>
                        <div data-character-section>
                            <FoodCostFaqSection />
                        </div>
                        <div data-character-section>
                            <NewsletterSubscribe variant='landing' source='landing/food-cost' compact />
                        </div>
                        <div>
                            <Footer landingPage='food-cost' className='mt-3 border-[#F5EEE4]/10 bg-[#17130E]' />
                        </div>
                    </div>
                </main>
                <FoodCostSideCharacter sectionRef={characterSectionsRef} heroRef={heroRef} />
                {showRouterPopup ? <HomeRouterPopup /> : null}
            </div>
        </div>
    )
}
